import { type SerializeFrom } from '@remix-run/node'
import { useRouteLoaderData } from '@remix-run/react'
import { type loader as rootLoader } from '#app/root.tsx'

/**
 * @description A function that checks if a value is a valid User object
 * @param user Any value to check
 * @returns True if it is true, and false if otherwise
 */
function isUser(user: any): user is SerializeFrom<typeof rootLoader>['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

/**
 * @description A function that calls the root loader and returns a user if found
 * @returns A user or null
 */
export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

/**
 * @description A function that calls the root loader and returns a user if found, or throws an error if otherwise
 * @returns A user or undefined
 */
export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Action = 'create' | 'read' | 'update' | 'delete' //The actions carried out by a user
type Entity = 'user' | 'note' //The entity worked on, say a user or a note
type Access = 'own' | 'any' | 'own,any' | 'any,own' //The access value of an entity, can be anyone or the owner or both
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

/**
 * @description A function that parses a permission string
 * @param permissionString The string with the permissions to parse
 * @returns An object detailing the action, entity and access-level required
 */
export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

/**
 * A function to check if the user has the required permissions(entity,action or access)
 * @param user The user object's roles to parse through
 * @param permission The permission string required for the user's permission
 * @returns True if the user is granted the permissions and false if otherwise
 */
export function userHasPermission(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some(role =>
		role.permissions.some(
			permission =>
				permission.entity === entity &&
				permission.action === action &&
				(!access || access.includes(permission.access)),
		),
	)
}

/**
 * @description A function to check if a user has a certain role
 * @param user The user object roles' to parse through
 * @param role The role required to search for
 * @returns True if the user has the required role, and false if otherwise
 */
export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some(r => r.name === role)
}
